import { useMemo } from "react";
import {
  MUIBox,
  Button,
  CobeeProvider,
  MUITypography,
  Divider,
  IconChevronChange,
  tokens,
  OptionsMenu,
  Label,
  Icon,
  IconKeys,
} from "@cobee/cobee-ui";
import { useTranslation } from "react-i18next";
import { QueryClientProvider } from "react-query";
import { useTreatments } from "@splitsoftware/splitio-react";
import { CountryIsoCode } from "v2/domain/common/enums/CountryISOCode";
import { MenuItem } from "@material-ui/core";
import useCompanies from "v2/ui/hooks/common/useCompanies";
import useChangeCompany from "v2/ui/hooks/common/useChangeCompany";
import featureFlagsDictionary from "infrastructure/split/feature-flags-dictionary";
import useGetCompanyFeatures from "v2/ui/hooks/common/useGetCompanyFeatures";
import {
  ManagerPermission,
  Permission,
} from "v2/domain/common/entities/ManagerPermission/ManagerPermission";
import useFindManagerPermissions from "v2/ui/hooks/common/useFindManagerPermissions/useFindManagerPermissions";
import { styles } from "./styles";
import { showCustomLogo } from "../../../../infrastructure/chosen-company";
import demoLogo from "../../../../assets/img/demo-logo.svg";
import happy from "../../../../assets/img/happy.svg";
import theme from "../../../infrastructure/common/theme";
import CobeeByPluxee from "../../../../assets/img/CobeeByPluxee.png";
import useGetCompany from "../../hooks/common/useGetCompany";
import { queryClient } from "../../Providers/QueryProvider";
import showLogoutModal from "../../../../usecases/commons/show-logout-modal";
import EsFlag from "../../../../assets/img/es-flag.svg";
import MxFlag from "../../../../assets/img/mx-flag.svg";
import PtFlag from "../../../../assets/img/pt-flag.svg";

const IMAGE_SIZE = "40px";
const HELP_LINK_ES = "https://helpcenter.cobee.io/hc/es-es";
const HELP_LINK_EN = "https://helpcenter.cobee.io/hc/en-150";
// eslint-disable-next-line no-shadow
enum SidebarOptions {
  HOME = "home",
  BENEFITS = "benefits",
  PACKAGES = "packages",
  EMPLOYEES = "employees",
  SETTINGS = "settings",
  REQUEST_BENEFITS = "requests-section",
  ANALYTICS = "analytics",
}

const getConfig = (
  showRequestBenefitsSection: boolean,
  showAnalyticsSection: boolean,
  loggedManagerPermission: ManagerPermission,
  packagesSectionBOEFFEnabled: boolean
) => {
  return {
    [SidebarOptions.HOME]: {
      label: "my-account-section.my-account",
      url: "/v2",
      icon: <Icon icon={IconKeys.HOME} />,
      shouldRender: true,
    },
    [SidebarOptions.BENEFITS]: {
      label: "benefits-section.benefits",
      url: packagesSectionBOEFFEnabled ? "/v2/benefits" : "/benefits",
      icon: <Icon icon={IconKeys.BENEFITS} />,
      shouldRender: loggedManagerPermission.hasPermission(
        Permission.CAN_ACCESS_BENEFITS_SECTION
      ),
    },
    [SidebarOptions.EMPLOYEES]: {
      label: "employees-section.employees",
      url: "/employees",
      icon: <Icon icon={IconKeys.USERS} />,
      shouldRender: loggedManagerPermission.hasPermission(
        Permission.CAN_ACCESS_EMPLOYEES_SECTION
      ),
    },
    [SidebarOptions.REQUEST_BENEFITS]: {
      label: "v2.section-titles.request-benefits",
      url: "/v2/requests",
      icon: <Icon icon={IconKeys.REQUEST} />,
      shouldRender:
        showRequestBenefitsSection &&
        loggedManagerPermission.hasPermission(
          Permission.CAN_ACCESS_BENEFITS_SECTION
        ),
    },
    [SidebarOptions.ANALYTICS]: {
      label: "v2.section-titles.analytics",
      url: "/v2/analytics",
      icon: <Icon icon={IconKeys.CHART} />,
      shouldRender:
        showAnalyticsSection &&
        loggedManagerPermission.hasPermission(
          Permission.CAN_ACCESS_ANALYTICS_SECTION
        ),
    },
    [SidebarOptions.SETTINGS]: {
      label: "v2.section-titles.settings",
      url: "/v2/settings",
      icon: <Icon icon={IconKeys.SETTINGS} />,
      shouldRender: true,
    },
  };
};

const COUNTRY_FLAGS = {
  [CountryIsoCode.SPAIN]: EsFlag,
  [CountryIsoCode.MEXICO]: MxFlag,
  [CountryIsoCode.PORTUGAL]: PtFlag,
};

const SidebarV2 = () => {
  const { t, i18n } = useTranslation();
  const { data: company } = useGetCompany();
  const { data: companies } = useCompanies();
  const treatments = useTreatments(
    [
      featureFlagsDictionary.BI_2406,
      featureFlagsDictionary.SMES_2223,
      featureFlagsDictionary.BI_7735,
    ],
    {
      countryISOCode: company?.countryIsoCode as string,
      companyId: company?.id as string,
    }
  );
  const showRequestBenefitsSection =
    treatments[featureFlagsDictionary.BI_2406].treatment === "on";

  const packagesSectionBOEFFEnabled =
    treatments[featureFlagsDictionary.SMES_2223].treatment === "on";

  const isAnalyticsEnabledForEveryOne =
    treatments[featureFlagsDictionary.BI_7735].treatment === "on";

  const { data: isCompanyFeatureEnabled } = useGetCompanyFeatures();
  const { data: loggedManagerPermission } = useFindManagerPermissions();

  const showAnalyticsSection =
    isAnalyticsEnabledForEveryOne ||
    isCompanyFeatureEnabled?.ANALYTICS_DASHBOARDS;

  const { mutate: changeCompany } = useChangeCompany();

  const allSelectableCompanies = useMemo(
    () => companies?.filter(({ id }) => id !== company?.id),
    [companies, company]
  );

  const urlMatches = (urls: string | string[]): boolean => {
    const { pathname } = window.location;
    const isActiveRequestFeatureFlagAndIsRecurrentPaymentSection =
      (pathname.includes("employee-requests") ||
        pathname.includes("recurrent-payments")) &&
      urls === "/v2/requests" &&
      showRequestBenefitsSection;

    const isInActiveRequestSection =
      !showRequestBenefitsSection ||
      (!pathname.includes("recurrent-payments") &&
        !pathname.includes("employee-requests") &&
        urls !== "v2/requests");

    const isBenefitsSection =
      urls === "/v2/benefits" && pathname === "/benefits";

    if (isActiveRequestFeatureFlagAndIsRecurrentPaymentSection) {
      return true;
    }
    if (typeof urls === "string") {
      return (
        (isInActiveRequestSection &&
          (pathname === urls || pathname.startsWith(urls))) ||
        isBenefitsSection
      );
    }

    return urls.some((url) => url === pathname);
  };

  const config = useMemo(() => {
    return loggedManagerPermission
      ? getConfig(
          showRequestBenefitsSection,
          !!showAnalyticsSection,
          loggedManagerPermission,
          packagesSectionBOEFFEnabled
        )
      : [];
  }, [
    showRequestBenefitsSection,
    showAnalyticsSection,
    loggedManagerPermission,
    packagesSectionBOEFFEnabled,
  ]);

  return (
    <QueryClientProvider client={queryClient}>
      <CobeeProvider theme={theme}>
        <MUIBox
          display="flex"
          flexDirection="column"
          height="100vh"
          flexShrink={0}
          overflow="hidden"
          sx={styles.container}
        >
          <MUIBox
            mb={3}
            pt={4.5}
            paddingX={2.25}
            justifyContent="center"
            display="flex"
            flexDirection="column"
            flexWrap="wrap"
          >
            <MUIBox
              height={IMAGE_SIZE}
              width={IMAGE_SIZE}
              mr={0.5}
              flexShrink={0}
            >
              <img
                width="100%"
                src={showCustomLogo(company?.id) ? demoLogo : happy}
                alt="logo"
              />
            </MUIBox>
            <MUIBox display="flex" flexDirection="row" alignItems="center">
              <MUITypography
                maxHeight="4.5em"
                textOverflow="ellipsis"
                overflow="hidden"
                fontWeight="500"
                marginLeft="8px !important"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: "3",
                  WebkitBoxOrient: "vertical",
                }}
              >
                {company?.name}
              </MUITypography>
              {Boolean(allSelectableCompanies?.length) && (
                <MUIBox ml={1}>
                  <OptionsMenu icon={<IconChevronChange />}>
                    {allSelectableCompanies?.map((companyToSelect) => {
                      return (
                        <MenuItem
                          key={companyToSelect.id}
                          onClick={() =>
                            changeCompany({ company: companyToSelect })
                          }
                        >
                          <MUIBox
                            maxWidth="236px"
                            display="flex"
                            alignItems="flex-start"
                            gap={1}
                          >
                            <img
                              src={
                                COUNTRY_FLAGS[companyToSelect.countryISO2Code]
                              }
                              alt={`${companyToSelect.countryISO2Code} flag`}
                            />
                            <MUIBox display="flex" flexDirection="column">
                              <MUITypography
                                component="span"
                                variant="body2"
                                fontWeight="500"
                              >
                                {companyToSelect.name}
                              </MUITypography>
                              <MUITypography
                                component="span"
                                variant="caption"
                                color={tokens.palette.grey[700]}
                              >
                                {companyToSelect.legalName}
                              </MUITypography>
                              <MUITypography
                                component="span"
                                variant="caption"
                                color={tokens.palette.grey[700]}
                              >
                                {companyToSelect.legalName}
                              </MUITypography>
                            </MUIBox>
                          </MUIBox>
                        </MenuItem>
                      );
                    })}
                  </OptionsMenu>
                </MUIBox>
              )}
            </MUIBox>
          </MUIBox>
          <MUIBox
            flexGrow={1}
            display="flex"
            flexDirection="column"
            alignItems="space-between"
            pr={2.25}
          >
            <MUIBox flexGrow={1}>
              {Object.keys(config).map((key) => {
                const { label, icon, url, isNew, shouldRender } = config[key];

                return (
                  shouldRender && (
                    <MUIBox sx={styles.optionContainer} key={key}>
                      <MUIBox
                        className="optionDecorator"
                        sx={
                          urlMatches(url)
                            ? {
                                ...styles.optionDecorator,
                                ...styles.selectedOptionDecorator,
                              }
                            : styles.optionDecorator
                        }
                      />
                      <Button
                        data-testid={label}
                        color="secondary"
                        variant="contained"
                        sx={
                          urlMatches(url)
                            ? {
                                ...styles.option,
                                ...styles.selectedOption,
                              }
                            : styles.option
                        }
                        onClick={() => window.location.assign(url)}
                        startIcon={icon}
                      >
                        <MUITypography
                          color={
                            urlMatches(url)
                              ? tokens.palette.common.white
                              : tokens.palette.grey.A700
                          }
                        >
                          {t(label)}
                        </MUITypography>
                        {isNew && (
                          <MUIBox ml={1} sx={styles.newLabel}>
                            <Label
                              text="NEW"
                              color={tokens.palette.tag.pink}
                              colorText={tokens.palette.accent.dark}
                            />
                          </MUIBox>
                        )}
                      </Button>
                    </MUIBox>
                  )
                );
              })}
            </MUIBox>
          </MUIBox>

          <MUIBox pb={7.5} paddingX={2.25}>
            <MUIBox
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
            >
              <Button
                color="secondary"
                variant="text"
                sx={{ ...styles.option, mb: 2 }}
                href={i18n.language === "es" ? HELP_LINK_ES : HELP_LINK_EN}
                target="_blank"
                startIcon={<Icon icon={IconKeys.QUESTION} />}
              >
                <MUITypography color={tokens.palette.grey.A700} variant="body1">
                  {t("v2.common.go-to-help")}
                </MUITypography>
              </Button>
              <Button
                color="secondary"
                variant="text"
                sx={styles.option}
                onClick={() => showLogoutModal()}
                startIcon={<Icon icon={IconKeys.LOGOUT} />}
              >
                <MUITypography color={tokens.palette.grey.A700} variant="body1">
                  {t("v2.common.close")}
                </MUITypography>
              </Button>
            </MUIBox>
            <Divider margin={25} />
            <MUIBox width={100} height={32} margin="auto">
              <img src={CobeeByPluxee} alt="cobee-by-pluxee" width="128px" />
            </MUIBox>
          </MUIBox>
        </MUIBox>
      </CobeeProvider>
    </QueryClientProvider>
  );
};

const WrappedSidebarV2 = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <SidebarV2 />
    </QueryClientProvider>
  );
};

export default WrappedSidebarV2;
